import React, { useEffect, useRef, useState, useCallback } from 'react';
import { preserveSpaces } from './ReportComponents';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TextStyle, Color } from '@tiptap/extension-text-style';
import { Extension } from '@tiptap/core';

// Custom font-size extension using TextStyle
const FontSize = Extension.create({
  name: 'fontSize',
  addOptions() { return { types: ['textStyle'] }; },
  addGlobalAttributes() {
    return [{
      types: this.options.types,
      attributes: {
        fontSize: {
          default: null,
          parseHTML: el => el.style.fontSize?.replace(/['"]+/g, '') || null,
          renderHTML: attrs => {
            if (!attrs.fontSize) return {};
            return { style: `font-size: ${attrs.fontSize}` };
          },
        },
      },
    }];
  },
  addCommands() {
    return {
      setFontSize: (size) => ({ chain }) =>
        chain().setMark('textStyle', { fontSize: size }).run(),
      unsetFontSize: () => ({ chain }) =>
        chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run(),
    };
  },
});

/**
 * Inline text editor for the A4 preview.
 *
 * - In view mode: renders static HTML (same as TextWithParagraphs).
 * - In edit mode: renders a TipTap editor that looks identical to the
 *   static HTML but is editable in place. On focus it notifies the parent
 *   so a floating toolbar can be shown.
 * - Has a toggle to switch between rich-text and raw HTML editing.
 */
const InlineTextEditor = ({ value, onChange, editMode, blockId, onFocus, onBlur, className = 'section-text' }) => {
  const containerRef = useRef(null);
  const [isActive, setIsActive] = useState(false);
  const [htmlMode, setHtmlMode] = useState(false);
  const [htmlSource, setHtmlSource] = useState('');
  const onFocusRef = useRef(onFocus);
  const onBlurRef = useRef(onBlur);
  onFocusRef.current = onFocus;
  onBlurRef.current = onBlur;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2] },
        codeBlock: false,
        code: false,
        blockquote: false,
        horizontalRule: false,
      }),
      TextStyle,
      Color,
      FontSize,
    ],
    content: value || '',
    editable: editMode,
    editorProps: {
      attributes: {
        class: `inline-tiptap-content ${className}`,
        spellcheck: 'true',
        lang: 'en-AU',
      },
    },
    onUpdate: ({ editor: ed }) => {
      if (onChange) onChange(ed.getHTML());
    },
    onFocus: ({ editor: ed }) => {
      setIsActive(true);
      if (onFocusRef.current) onFocusRef.current(blockId, ed, containerRef.current);
    },
    onBlur: () => {
      setIsActive(false);
      if (onBlurRef.current) onBlurRef.current(blockId);
    },
  });

  // Sync external value changes (e.g. loading new content from server)
  useEffect(() => {
    if (editor && !editor.isFocused && value !== editor.getHTML()) {
      editor.commands.setContent(value || '', false);
    }
  }, [value, editor]);

  // Update editable state when editMode changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(editMode);
    }
  }, [editMode, editor]);

  // Toggle into HTML mode
  const enterHtmlMode = useCallback(() => {
    const currentHtml = editor ? editor.getHTML() : (value || '');
    setHtmlSource(currentHtml);
    setHtmlMode(true);
  }, [editor, value]);

  // Toggle back to rich-text mode — apply HTML changes
  const exitHtmlMode = useCallback(() => {
    if (editor) {
      editor.commands.setContent(htmlSource, false);
    }
    if (onChange) onChange(htmlSource);
    setHtmlMode(false);
  }, [editor, htmlSource, onChange]);

  // If not in edit mode, render static HTML (same as TextWithParagraphs)
  if (!editMode) {
    if (!value) return null;
    if (/<[a-z][\s\S]*>/i.test(value)) {
      return <div className={className} dangerouslySetInnerHTML={{ __html: preserveSpaces(value) }} />;
    }
    // Legacy plain-text fallback
    const paragraphs = value.split(/\n\n+/);
    return (
      <>
        {paragraphs.map((para, i) => (
          <p key={i} className={className}>
            {para.split('\n').map((line, j, arr) => (
              <React.Fragment key={j}>
                {line}
                {j < arr.length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
        ))}
      </>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`inline-text-editor-wrap ${isActive ? 'is-editing' : ''}`}
    >
      {htmlMode ? (
        <textarea
          className="html-source-editor"
          value={htmlSource}
          onChange={(e) => setHtmlSource(e.target.value)}
          spellCheck={false}
        />
      ) : (
        <EditorContent editor={editor} />
      )}
      <button
        className={`html-toggle-btn ${htmlMode ? 'active' : ''}`}
        onClick={htmlMode ? exitHtmlMode : enterHtmlMode}
        onMouseDown={(e) => e.preventDefault()}
        title={htmlMode ? 'Switch to rich text' : 'Edit HTML source'}
      >
        {htmlMode ? 'Rich Text' : '</>'}
      </button>
    </div>
  );
};

export default InlineTextEditor;
